import { type BookmarkedPages, OoxmlRange } from "@common/utils/redline/ooxml-range";

import { readRangeOoxmlsInGroups } from "./range-ooxml-read-groups";

export const MIN_ENTRIES_FOR_ONE_DOCUMENT_READ = 4;

interface OoxmlRead {
  value: string;
}

interface OoxmlReadableRange {
  getOoxml(): OoxmlRead;
}

export interface BatchEntryRead {
  bookmarkNames: string[];
  range: OoxmlReadableRange;
  readFromWordOnly: boolean;
}

interface DocumentReadingContext {
  sync(): Promise<void>;
  document: { body: { getOoxml(): OoxmlRead } };
}

async function readBookmarkedPagesOfDocumentOrNull(context: DocumentReadingContext): Promise<BookmarkedPages | null> {
  try {
    const bodyRead = context.document.body.getOoxml();
    await context.sync();
    return OoxmlRange.bookmarkedPagesOf(bodyRead.value);
  } catch (documentReadError) {
    console.warn("Word rejected the one read of the document, reading each entry's range instead:", documentReadError);
    return null;
  }
}

export async function readCurrentOoxmlsOfBatchEntries(
  context: DocumentReadingContext,
  entries: BatchEntryRead[],
  maxGroupChars: number,
  onDocumentPages?: (documentPages: BookmarkedPages) => void,
): Promise<string[]> {
  const documentPages = entries.length < MIN_ENTRIES_FOR_ONE_DOCUMENT_READ ? null : await readBookmarkedPagesOfDocumentOrNull(context);
  if (documentPages === null) {
    return readRangeOoxmlsInGroups(context, entries.map(({ range }) => range), maxGroupChars);
  }
  onDocumentPages?.(documentPages);
  const currentOoxmls: Array<string | null> = entries.map(({ bookmarkNames, readFromWordOnly }) => {
    if (readFromWordOnly) return null;
    const page = documentPages.pageOf(bookmarkNames);
    return page !== null && page.kind === "page" ? page.packageOoxml : null;
  });
  const entryIndexesAskedOfWord = currentOoxmls.flatMap((currentOoxml, entryIndex) => (currentOoxml === null ? [entryIndex] : []));
  const readFromWord = await readRangeOoxmlsInGroups(context, entryIndexesAskedOfWord.map((entryIndex) => entries[entryIndex].range), maxGroupChars);
  entryIndexesAskedOfWord.forEach((entryIndex, readIndex) => {
    currentOoxmls[entryIndex] = readFromWord[readIndex];
  });
  return currentOoxmls as string[];
}
